import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, CheckCircle, AlertTriangle, Loader2, ArrowLeft, Home } from 'lucide-react'; 
import api from '../../api/axios';
import { useTranslation } from 'react-i18next';

interface Question {
  id: string;
  text: string;
  options: string[];
  imageUrl?: string;
}

const TakeExam: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [exam, setExam] = useState<any>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchExam = async () => {
      try {
        const res = await api.get(`/exams/${id}`);
        setExam(res.data);
        setQuestions(res.data.questions.map((q: any) => q.question));
      } catch (err: any) {
        setError(err.response?.data?.message || t('failed_to_load_exam', 'Failed to load exam'));
      } finally {
        setLoading(false);
      }
    };
    fetchExam();
  }, [id]);

  const handleSelectOption = (option: string) => {
    setAnswers(prev => ({ ...prev, [questions[currentIndex].id]: option }));
  };

  const handleSubmit = async () => {
    if (!window.confirm(t('confirm_submit_exam', "Are you sure you want to submit your exam?"))) return;
    
    setSubmitting(true);
    try {
      const payloadArray = Object.keys(answers).map(qId => ({
        questionId: qId,
        selectedOption: answers[qId]
      }));

      const res = await api.post('/exams/submit', {
        examId: id,
        answers: payloadArray
      });

      setResult(res.data);
    } catch (err: any) {
      setError(err.response?.data?.message || t('failed_to_submit_exam', 'Failed to submit exam'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-900 via-slate-800 to-indigo-950 flex items-center justify-center text-indigo-500 relative">
      <Loader2 size={40} className="animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-900 via-slate-800 to-indigo-950 flex items-center justify-center p-6 relative">
      <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-8 rounded-[2rem] text-center max-w-sm">
        <AlertTriangle size={40} className="mx-auto mb-4" />
        <h2 className="text-lg font-black uppercase tracking-widest mb-2">{t('error', 'Error')}</h2>
        <p className="text-xs mb-6">{error}</p>
        <button onClick={() => navigate('/student/home')} className="px-6 py-2 bg-slate-800 text-white rounded-xl text-[10px] font-black uppercase">{t('return_home', 'Return Home')}</button>
      </div>
    </div>
  );

  if (result) {
    return (
      <div className="min-h-screen bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-900 via-slate-800 to-indigo-950 flex items-center justify-center p-6 relative">
        <div className="bg-slate-950 border border-slate-800 p-10 rounded-[2.5rem] text-center max-w-md w-full shadow-2xl">
          <div className="w-20 h-20 bg-indigo-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-indigo-500/20">
            <CheckCircle size={40} className="text-indigo-400" />
          </div>
          <h2 className="text-2xl font-black uppercase tracking-tighter text-white mb-2">{t('exam_completed', 'Exam Completed')}</h2>
          <p className="text-sm text-slate-400 font-bold mb-6">{t('final_score_calculated', 'Your final score has been calculated.')}</p>
          
          <div className={`text-4xl font-black mb-8 p-6 rounded-2xl border ${result.score >= 50 ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>
            {Number(result.score).toFixed(0)}%
          </div>

          {result.weakTopics?.length > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/20 p-4 rounded-xl mb-8 text-left">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={16} className="text-amber-400" />
                <span className="text-[10px] font-black uppercase tracking-widest text-amber-400">{t('weak_topics_detected', 'Weak Topics Detected')}</span>
              </div>
              <p className="text-xs font-bold text-slate-300">{t('you_struggled_with', 'You struggled with:')} <span className="text-white">{result.weakTopics.join(', ')}</span></p>
              
              <Link to="/student/post-job" className="mt-4 block w-full py-3 bg-amber-500 text-slate-900 hover:bg-amber-400 text-center font-black uppercase tracking-wider text-[10px] rounded-lg shadow-xl transition-all active:scale-95">
                {t('book_tutor_for_review', 'Book a Tutor for Review')}
              </Link>
            </div>
          )}

          <button onClick={() => navigate('/student/home')} className="w-full py-4 bg-slate-800 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-700 transition-colors">
            {t('return_to_dashboard', 'Return to Dashboard')}
          </button>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentIndex];

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-900 via-slate-800 to-indigo-950 text-white flex flex-col relative">
      <header className="h-16 shrink-0 flex items-center px-4 md:px-8 border-b border-slate-800 bg-slate-950/80 backdrop-blur-xl z-50">
        <button onClick={() => { if(window.confirm(t('leave_exam_warning', "Leave exam? Progress will be lost."))) navigate('/student/home'); }} className="text-slate-400 hover:text-white flex items-center gap-2 text-xs font-black uppercase tracking-widest transition-colors">
          <ArrowLeft size={16} /> {t('exit', 'Exit')}
        </button>
        <div className="ml-auto text-xs font-black text-slate-400 uppercase tracking-widest">
          {t('question', 'Question')} {currentIndex + 1} {t('of', 'of')} {questions.length}
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center p-4 md:p-8 overflow-y-auto">
        <div className="w-full max-w-3xl bg-slate-950 border border-slate-800 rounded-[2rem] p-6 md:p-10 shadow-2xl">
          
          <h2 className="text-lg md:text-xl font-bold leading-relaxed text-slate-200 mb-8">
            {currentQ?.text}
          </h2>

          {currentQ?.imageUrl && (
            <img src={currentQ.imageUrl} alt="Reference" className="mb-8 rounded-xl max-h-64 object-contain mx-auto border border-slate-800" />
          )}

          <div className="grid gap-3">
            {currentQ?.options.map((opt, i) => {
              const isSelected = answers[currentQ.id] === opt;
              return (
                <button 
                  key={i} 
                  onClick={() => handleSelectOption(opt)}
                  className={`p-4 text-left rounded-xl text-sm font-bold transition-all border-2 ${
                    isSelected 
                      ? 'bg-indigo-600 border-indigo-500 text-white shadow-lg' 
                      : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-indigo-500/50 hover:text-white'
                  }`}
                >
                  <span className="inline-block w-6 h-6 text-center rounded-md bg-black/20 mr-3 text-[10px] leading-6 uppercase">{String.fromCharCode(65 + i)}</span>
                  {opt}
                </button>
              );
            })}
          </div>

        </div>

        <div className="w-full max-w-3xl flex items-center justify-between mt-8">
          <button 
            disabled={currentIndex === 0} 
            onClick={() => setCurrentIndex(p => p - 1)} 
            className="px-6 py-3 bg-slate-800 text-white rounded-xl text-[10px] font-black uppercase tracking-widest disabled:opacity-30 flex items-center gap-2 hover:bg-slate-700 transition-colors"
          >
            <ChevronLeft size={16} /> {t('prev', 'Prev')}
          </button>

          {currentIndex === questions.length - 1 ? (
            <button 
              onClick={handleSubmit} 
              disabled={submitting || Object.keys(answers).length !== questions.length}
              className="px-8 py-3 bg-emerald-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-emerald-500 shadow-xl transition-all active:scale-95"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
              {t('submit_exam', 'Submit Exam')}
            </button>
          ) : (
            <button 
              onClick={() => setCurrentIndex(p => p + 1)} 
              className="px-6 py-3 bg-indigo-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 hover:bg-indigo-500 shadow-xl transition-all active:scale-95"
            >
              {t('next', 'Next')} <ChevronRight size={16} />
            </button>
          )}
        </div>
      </main>

      <button onClick={() => navigate('/student/home')} className="fixed bottom-6 right-6 bg-indigo-600 hover:bg-indigo-500 text-white p-4 rounded-full shadow-2xl shadow-indigo-500/30 transition-all hover:-translate-y-1 z-50 group flex items-center gap-0 overflow-hidden">
        <Home size={24} />
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs group-hover:ml-3 transition-all duration-300 ease-in-out whitespace-nowrap font-bold text-sm uppercase tracking-wider">{t('dashboard', 'Dashboard')}</span>
      </button>
    </div>
  );
};

export default TakeExam;